import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Typography } from '@gapartners/ui';
import { api } from '@/store/api';
import { DirectConnectLinkSection } from './customer-info/DirectConnectLinkSection';
import { ClientSpaceWorkspaceLink } from './ClientSpaceWorkspaceLink';
import type { CompanySearchResult } from './customer-info/types';

export interface HistoricalCallCustomerLinkSectionProps {
  contactId: string;
  workspaceId?: string | null;
  onLinked: () => void;
}

export function HistoricalCallCustomerLinkSection({
  contactId,
  workspaceId,
  onLinked,
}: HistoricalCallCustomerLinkSectionProps) {
  const { t } = useTranslation();
  const [companyQuery, setCompanyQuery] = useState('');
  const [linkError, setLinkError] = useState<string | null>(null);
  const [linkSectionExpanded, setLinkSectionExpanded] = useState(!workspaceId);

  useEffect(() => {
    if (workspaceId) {
      setLinkSectionExpanded(false);
    }
  }, [workspaceId]);

  const [
    triggerSearch,
    { data: searchResults, isFetching: searching, isSuccess: searchSucceeded },
  ] = api.useLazySearchCustomersByCompanyNameQuery();
  const [linkCustomer, { isLoading: linking }] =
    api.useLinkCustomerToCallMutation();

  const { data: customerData } = api.useGetCustomerInformationQuery(
    { workSpaceID: workspaceId ? String(workspaceId) : '' },
    { skip: !workspaceId }
  );

  const linkedCompanyName =
    customerData?.companyName ??
    (workspaceId ? `Workspace ${workspaceId}` : null);

  const handleSearch = () => {
    const q = companyQuery.trim();
    if (!q) return;
    setLinkError(null);
    void triggerSearch({ companyName: q });
  };

  const handleLink = async (organizationId: number) => {
    setLinkError(null);
    try {
      await linkCustomer({
        contactId,
        body: { workspaceId: organizationId },
      }).unwrap();
      setLinkSectionExpanded(false);
      setCompanyQuery('');
      onLinked();
    } catch {
      setLinkError(t('components.contactCenter.directConnectLinkFailed'));
    }
  };

  const linkSection = (
    <DirectConnectLinkSection
      t={t}
      companyQuery={companyQuery}
      setCompanyQuery={setCompanyQuery}
      searching={searching}
      linking={linking}
      searchSucceeded={searchSucceeded}
      results={(searchResults?.customers ?? []) as CompanySearchResult[]}
      linkError={linkError}
      onSearch={handleSearch}
      onLink={organizationId => {
        void handleLink(organizationId);
      }}
    />
  );

  return (
    <Box
      sx={{
        mt: 1,
        p: 1.5,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
      }}
    >
      <Typography variant="subtitle2" fontWeight={600}>
        {t('components.contactCenter.customer')}
      </Typography>

      {linkedCompanyName ? (
        <>
          <Typography variant="body2" color="text.primary">
            <ClientSpaceWorkspaceLink
              workspaceId={workspaceId}
              sx={{ color: 'text.primary' }}
            >
              {linkedCompanyName}
            </ClientSpaceWorkspaceLink>
          </Typography>
          <Box>
            <Button
              variant="text"
              size="small"
              onClick={() => setLinkSectionExpanded(!linkSectionExpanded)}
              sx={{ textTransform: 'none', p: 0, minWidth: 0 }}
            >
              <Typography variant="body2" fontWeight={600}>
                {t('components.contactCenter.changeWorkspace')}
                {linkSectionExpanded ? ' ▾' : ' ▸'}
              </Typography>
            </Button>
            {linkSectionExpanded && <Box sx={{ mt: 0.5 }}>{linkSection}</Box>}
          </Box>
        </>
      ) : (
        <>
          <Typography variant="body2" color="text.secondary">
            {t('components.contactCenter.linkCustomerBeforeCase')}
          </Typography>
          {linkSection}
        </>
      )}
    </Box>
  );
}
